import React, { useEffect, useRef, useState } from 'react';
import pt_br from '@fullcalendar/core/locales/pt-br';
import dayGridPlugin from '@fullcalendar/daygrid';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import multiMonthPlugin from '@fullcalendar/multimonth';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';

import FullCalendar from '@fullcalendar/react';
import { AppointmentUseCase } from '../../../data/useCase/AppointmentUseCase';
import { Tooltip } from 'react-tooltip';
import { Card, Col, Row } from 'react-bootstrap';
import { StatusApontamento } from '../../../main/constant/StatusAgendamentoConstant';
import { EndpointsConstant } from '../../../main/constant/EndpointConstant';

interface CalendarComponentComponentProps {
  rangeTimeDay: {
    start: string;
    end: string;
  };
  onClickEvent: (params?: any) => Promise<any>;
  onClickDate: (params?: any) => any;
  numeroCalendario: number;
  listProfessional: any[];
}

const CalendarComponent: React.FC<CalendarComponentComponentProps> = ({ numeroCalendario, rangeTimeDay, onClickEvent, onClickDate, listProfessional }) => {
  const ENDPOINT_APPOINTMENTS = EndpointsConstant.appointments;
  const calendarRef = useRef<null | any>(null);

  const loadFromLocalStorage = (key: string) => {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  };

  const getEvents = async (info: any, successCallback: any) => {
    const statusDefault = 'A,S,N,I,O';

    // Carregamento dos dados de profissionais e status salvos no localStorage (exemplo para numeroCalendario === 0)
    const savedProfessionals = loadFromLocalStorage(`calendario_selected_professionals_${numeroCalendario}`);
    const savedStatuses = loadFromLocalStorage(`calendario_selected_statuses_${numeroCalendario}`);

    const savedProfessionals_ids = savedProfessionals.length > 0 ? savedProfessionals.map((item: any) => item.id).join(', ') : '0';
    const savedStatuses_ids = savedStatuses.length > 0 ? savedStatuses.map((item: any) => item.id).join(', ') : statusDefault;

    await new AppointmentUseCase(
      `${ENDPOINT_APPOINTMENTS}/?start=${info.start.toISOString().split('T')[0]}&end=${info.end.toISOString().split('T')[0]}&status=${savedStatuses_ids}&professionals=${savedProfessionals_ids}`
    )
      .Get()
      .then((resp) => {
        // Adiciona a cor ao retorno com base no status
        const appointmentsWithColor = resp.body.map((appointment: any) => {
          const statusDetails = StatusApontamento.find((status) => status.id === appointment.status);
          return {
            ...appointment,
            color: statusDetails ? statusDetails.cor : null, // Adiciona a cor ou null se não encontrado
          };
        });
        successCallback(appointmentsWithColor);
      });
  };

  const renderEventContent = (eventInfo: any) => {
    let event = eventInfo.event.extendedProps;
    const getStatusDetails = (status: string) => {
      const statusDetail = StatusApontamento.find((item) => item.id === status);
      return statusDetail
        ? { name: statusDetail.label, color: statusDetail.cor }
        : {
            name: 'Desconhecido',
            color: '#aaa9a9',
          };
    };

    const statusDetails = getStatusDetails(event.status);

    return (
      <>
        <a
          data-tooltip-id={`tooltip-calendar-${event.scheduledAt}`}
          data-tooltip-variant="warning"
          style={{
            textDecoration: 'none',
            color: '#333',
            fontWeight: 'bold',
            cursor: 'pointer',
            fontSize: '0.9em',
            fontFamily: 'Arial, sans-serif',
            display: 'inline-block',
            width: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          <b>
            {eventInfo.timeText} {event.patientName}
          </b>
        </a>

        <Tooltip id={`tooltip-calendar-${event.scheduledAt}`} data-tooltip-variant="warning">
          <div>
            <Row>
              <Col>
                <strong style={{ color: '#ffffff', fontFamily: 'Roboto, sans-serif' }}>Profissional:</strong>
                <span style={{ color: '#ffffff', fontFamily: 'Lato, sans-serif' }}>{` ${event.professionalName}`}</span>
              </Col>
            </Row>
            <Row>
              <Col>
                <strong style={{ color: '#ffffff', fontFamily: 'Roboto, sans-serif' }}>Paciente:</strong>
                <span style={{ color: '#ffffff', fontFamily: 'Lato, sans-serif' }}>{` ${event.patientName}`}</span>
              </Col>
            </Row>

            <Row>
              <Col>
                <strong style={{ color: '#ffffff', fontFamily: 'Roboto, sans-serif' }}>Status:</strong>
                <span style={{ color: '#ffffff', fontFamily: 'Lato, sans-serif' }}>{` ${statusDetails.name}`}</span>
              </Col>
            </Row>

            <Row>
              <Col>
                <strong style={{ color: '#ffffff', fontFamily: 'Roboto, sans-serif' }}>OBS:</strong>
                <span
                  style={{
                    color: '#ffffff',
                    fontFamily: 'Lato, sans-serif',
                  }}>{` ${event.observation || 'Nenhuma observação'}`}</span>
              </Col>
            </Row>
          </div>
        </Tooltip>
      </>
    );
  };

  const handleMouseEnter = (info: any) => {};

  const handleMouseLeave = (info: any) => {};

  const LOCAL_STORAGE_KEY = `calendario_view_${numeroCalendario}`;

  const getInitialView = () => localStorage.getItem(LOCAL_STORAGE_KEY) || 'timeGridDay';

  const updateLocalStorageView = (viewType: string) => localStorage.setItem(LOCAL_STORAGE_KEY, viewType);

  useEffect(() => {
    const initialView = getInitialView();
    const calendarAPI = calendarRef.current?.getApi();
    if (calendarAPI) {
      calendarAPI.changeView(initialView);
    }
  }, []);

  const [calendarTitle, setCalendarTitle] = useState(''); // Novo estado para o título

  const handleViewChange = (view: any) => {
    updateLocalStorageView(view.view.type);
    setCalendarTitle(view.view.calendar.view.title); // Atualiza o título do calendário
  };

  useEffect(() => {
    const initialView = getInitialView();
    const calendarAPI = calendarRef.current?.getApi();
    if (calendarAPI) {
      calendarAPI.changeView(initialView);
      setCalendarTitle(calendarAPI.view.title); // Define o título inicial do calendário
    }
  }, []);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  return (
    <Card className="my-4 shadow-sm">
      <Card.Header className="text-white" style={{ background: '#9459d0' }}>
        <h5>Calendário de Agendamentos - {calendarTitle}</h5>
      </Card.Header>
      <Card.Body className="p-3" style={{ width: '100%' }}>
        <FullCalendar
          ref={calendarRef}
          allDaySlot={false}
          slotMinTime={rangeTimeDay.start}
          slotMaxTime={rangeTimeDay.end}
          themeSystem="bootstrap5"
          locales={[pt_br]}
          plugins={[dayGridPlugin, bootstrap5Plugin, multiMonthPlugin, interactionPlugin, timeGridPlugin, listPlugin]}
          headerToolbar={{
            left: isMobile ? 'anterior,proximo' : 'anterior,proximo,hoje',
            right: isMobile ? 'timeGridWeek,timeGridDay' : 'dayGridMonth,timeGridWeek,timeGridDay,listMonth',
          }}
          initialView={getInitialView()}
          editable={false}
          showNonCurrentDates={false}
          selectable={false}
          selectMirror={false}
          dayMaxEvents={false}
          events={(info, successCallback) => getEvents(info, successCallback)}
          eventClick={onClickEvent}
          dateClick={onClickDate}
          eventContent={renderEventContent}
          eventMouseEnter={handleMouseEnter}
          eventMouseLeave={handleMouseLeave}
          datesSet={handleViewChange}
          customButtons={{
            proximo: {
              text: isMobile ? '>' : 'Próximo',
              click: () => calendarRef.current?.getApi()?.next(),
            },
            anterior: {
              text: isMobile ? '<' : 'Anterior',
              click: () => calendarRef.current?.getApi()?.prev(),
            },
            hoje: {
              text: 'Hoje',
              click: () => calendarRef.current?.getApi()?.today(),
            },
          }}
        />
      </Card.Body>
    </Card>
  );
};

export default CalendarComponent;
